import { useState } from "react";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import {
  HOURS_OF_THE_DAY,
  SalesHeatmapCell,
  WEEKDAYS_FROM_MONDAY,
} from "./salesBreakdowns.ts";
import { formatCount, formatEuros } from "./salesFormat.ts";
import { SalesPeriod } from "./salesPeriod.ts";
import {
  EmptyStatistics,
  LoadingBlock,
  StatisticsCard,
} from "./StatisticsCard.tsx";
import { CsvButton } from "./CsvButton.tsx";
import { downloadSalesCsv, toCsv } from "./salesCsv.ts";

const EMPTY_CELL_COLOR = "#f3f4f6";
const SEQUENTIAL_BLUES = [
  "#cde2fb",
  "#9ec5f4",
  "#6da7ec",
  "#3987e5",
  "#256abf",
  "#184f95",
  "#0d366b",
];
const HOURS_WITH_LABEL = [0, 6, 12, 18];

const WEEKDAY_NAMES: Record<number, string> = {
  0: "Domenica",
  1: "Lunedì",
  2: "Martedì",
  3: "Mercoledì",
  4: "Giovedì",
  5: "Venerdì",
  6: "Sabato",
};

function colorOf(orderCount: number, busiestCount: number): string {
  if (orderCount === 0) return EMPTY_CELL_COLOR;
  const step = Math.ceil((orderCount / busiestCount) * SEQUENTIAL_BLUES.length);
  return SEQUENTIAL_BLUES[Math.min(SEQUENTIAL_BLUES.length, step) - 1];
}

function describeCell(cell: SalesHeatmapCell): string {
  const hours = `${String(cell.hour).padStart(2, "0")}:00–${String(cell.hour + 1).padStart(2, "0")}:00`;
  return `${WEEKDAY_NAMES[cell.weekday]}, ${hours} · ${formatCount(cell.orderCount)} ${cell.orderCount === 1 ? "ordine" : "ordini"} · ${formatEuros(cell.soldInCents)}`;
}

export function SalesHeatmapCard({
  cells,
  period,
  isLoading,
}: {
  cells: SalesHeatmapCell[];
  period: SalesPeriod;
  isLoading: boolean;
}) {
  const [hoveredCell, setHoveredCell] = useState<SalesHeatmapCell | null>(null);
  const busiestCount = Math.max(0, ...cells.map((cell) => cell.orderCount));
  const busiestCell = cells.find((cell) => cell.orderCount === busiestCount);

  const exportCsv = () =>
    downloadSalesCsv({
      section: "giorni-e-ore",
      period,
      csv: toCsv(WEEKDAYS_FROM_MONDAY, [
        { header: "Giorno", valueOf: (weekday) => WEEKDAY_NAMES[weekday] },
        ...HOURS_OF_THE_DAY.map((hour) => ({
          header: `${hour}:00`,
          valueOf: (weekday: number) =>
            cells.find((cell) => cell.weekday === weekday && cell.hour === hour)
              ?.orderCount ?? 0,
        })),
      ]),
    });

  return (
    <StatisticsCard
      title="Quando si vende"
      info={SALES_DEFINITIONS.heatmap}
      description="Ordini conclusi nel periodo per giorno della settimana e ora"
      actions={busiestCount > 0 ? <CsvButton onClick={exportCsv} /> : undefined}
    >
      {isLoading ? (
        <LoadingBlock className="h-48 w-full" />
      ) : busiestCount === 0 ? (
        <EmptyStatistics>Nessun ordine concluso nel periodo</EmptyStatistics>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="overflow-x-auto">
            <div
              className="grid min-w-[36rem] gap-0.5"
              style={{
                gridTemplateColumns: `5.5rem repeat(${HOURS_OF_THE_DAY.length}, minmax(0, 1fr))`,
              }}
              onMouseLeave={() => setHoveredCell(null)}
            >
              <span />
              {HOURS_OF_THE_DAY.map((hour) => (
                <span
                  key={hour}
                  className="text-[10px] text-gray-400 text-center"
                >
                  {HOURS_WITH_LABEL.includes(hour) ? `${hour}:00` : ""}
                </span>
              ))}
              {WEEKDAYS_FROM_MONDAY.map((weekday) => (
                <div key={weekday} className="contents">
                  <span className="pr-2 text-xs text-gray-500 leading-6">
                    {WEEKDAY_NAMES[weekday]}
                  </span>
                  {HOURS_OF_THE_DAY.map((hour) => {
                    const cell = cells.find(
                      (each) => each.weekday === weekday && each.hour === hour,
                    ) as SalesHeatmapCell;
                    return (
                      <button
                        key={hour}
                        type="button"
                        aria-label={describeCell(cell)}
                        onMouseEnter={() => setHoveredCell(cell)}
                        onFocus={() => setHoveredCell(cell)}
                        className="h-6 rounded-sm outline-offset-1 hover:outline hover:outline-2 hover:outline-gray-800 focus:outline focus:outline-2 focus:outline-gray-800"
                        style={{
                          backgroundColor: colorOf(
                            cell.orderCount,
                            busiestCount,
                          ),
                        }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
            <span className="min-h-4">
              {hoveredCell
                ? describeCell(hoveredCell)
                : busiestCell &&
                  `Fascia con più ordini: ${describeCell(busiestCell)}`}
            </span>
            <span className="flex items-center gap-1">
              Meno
              {SEQUENTIAL_BLUES.map((color) => (
                <span
                  key={color}
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
              Più
            </span>
          </div>
        </div>
      )}
    </StatisticsCard>
  );
}
